import Glide, { Swipe } from '@glidejs/glide/dist/glide.modular.esm'

const MAX_PER_VIEW = 3

// columns per page for the "double" (2-row) layout — must match the
// grid-template-columns breakpoints in ImageSlider.scss, or the page size
// here and the actual number of grid slots on screen fall out of sync
function getDoubleColumns() {
    if (window.matchMedia('(max-width: 576px)').matches) {
        return 1
    }
    if (window.matchMedia('(max-width: 991px)').matches) {
        return 2
    }
    return 3
}

function initSingleLayout(galleryEl) {
    const listEl = galleryEl.querySelector('[data-image-slider-list]')

    // nothing to slide (e.g. collection still empty while editing) — skip
    // mounting glide entirely rather than risk it erroring on 0 slides
    if (!listEl || !listEl.querySelector('.glide__slide')) {
        return
    }

    const boxes = galleryEl.querySelectorAll('.image-slider__box')

    // fewer boxes than fit in a row — lay them out as a static, wrapping
    // grid instead of mounting glide (glide's carousel type clones slides
    // to fill up the perView slots when there are fewer real slides than
    // that, which visually repeats a single box several times)
    if (boxes.length <= MAX_PER_VIEW) {
        listEl.classList.add('image-slider__list--static')
        galleryEl.querySelectorAll('[data-image-slider-prev], [data-image-slider-next]').forEach((btn) => {
            btn.style.display = 'none'
        })
        return
    }

    const prevButton = galleryEl.querySelector('[data-image-slider-prev]')
    const nextButton = galleryEl.querySelector('[data-image-slider-next]')

    const getSliderSettings = () => {
        if (window.matchMedia('(max-width: 576px)').matches) {
            return { perView: 1, gap: 12 }
        }
        if (window.matchMedia('(max-width: 991px)').matches) {
            return { perView: 2, gap: 16 }
        }
        return { perView: MAX_PER_VIEW, gap: 20 }
    }

    const sliderSettings = getSliderSettings()

    // "carousel" (with its clone-based infinite loop) measurably
    // misaligned the starting slide by a fixed offset unrelated to any of
    // our own config (confirmed via direct DOM measurement — no peek, no
    // stray CSS, glide's own clone positioning was the cause). "slider"
    // doesn't clone at all, which sidesteps that entirely. No rewind
    // either now (client request, applies to every slider on the site) —
    // bound:true stops firmly at the last slide instead of wrapping back.
    const glide = new Glide(listEl, {
        type: 'slider',
        bound: true,
        // glide.js defaults "rewind" to true even for "slider" type —
        // without this it silently wraps around and never disables the
        // prev/next buttons at the boundaries
        rewind: false,
        perView: sliderSettings.perView,
        gap: sliderSettings.gap,
        animationDuration: 400,
        // Neos.Neos:ContentCollection wraps the boxes in its own
        // ".neos-contentcollection" div — treat it as the slides track
        classes: { slides: 'neos-contentcollection' },
    })

    const updateArrows = () => {
        prevButton?.classList.toggle('is-disabled', glide.index === 0)
        nextButton?.classList.toggle('is-disabled', glide.index + glide.settings.perView >= boxes.length)
    }

    if (prevButton) {
        prevButton.addEventListener('click', () => glide.go('<'))
    }
    if (nextButton) {
        nextButton.addEventListener('click', () => glide.go('>'))
    }

    glide.on(['mount.after', 'run'], updateArrows)
    glide.mount({ Swipe })

    window.addEventListener('resize', () => {
        const nextSettings = getSliderSettings()
        glide.update({ perView: nextSettings.perView, gap: nextSettings.gap })
    })
}

function initDoubleLayout(galleryEl) {
    const listEl = galleryEl.querySelector('[data-image-slider-list]')
    const slides = Array.from(listEl ? listEl.querySelectorAll('.glide__slide') : [])

    if (!slides.length) {
        return
    }

    const prevButton = galleryEl.querySelector('[data-image-slider-prev]')
    const nextButton = galleryEl.querySelector('[data-image-slider-next]')

    let columns = getDoubleColumns()
    let pageSize = columns * 2
    let page = 0

    function render() {
        const pageCount = Math.ceil(slides.length / pageSize)
        // clamp in case a resize shrank pageSize enough that the current
        // page index would otherwise point past the last page
        page = Math.min(page, pageCount - 1)

        slides.forEach((slide, index) => {
            const slidePage = Math.floor(index / pageSize)
            slide.style.display = slidePage === page ? '' : 'none'
        })

        const hasMultiplePages = pageCount > 1
        if (prevButton) {
            prevButton.style.display = hasMultiplePages ? '' : 'none'
            // no loop (client request, applies to every slider on the
            // site) — hidden outright at either end, not just dimmed
            prevButton.classList.toggle('is-disabled', page === 0)
        }
        if (nextButton) {
            nextButton.style.display = hasMultiplePages ? '' : 'none'
            nextButton.classList.toggle('is-disabled', page >= pageCount - 1)
        }
    }

    function goToPage(direction) {
        const pageCount = Math.ceil(slides.length / pageSize)
        // was "(page + direction + pageCount) % pageCount" — wrapped
        // around past either end instead of stopping there
        page = Math.max(0, Math.min(pageCount - 1, page + direction))
        render()
    }

    if (prevButton) {
        prevButton.addEventListener('click', () => goToPage(-1))
    }
    if (nextButton) {
        nextButton.addEventListener('click', () => goToPage(1))
    }

    window.addEventListener('resize', () => {
        const nextColumns = getDoubleColumns()
        if (nextColumns !== columns) {
            columns = nextColumns
            pageSize = columns * 2
            page = 0
            render()
        }
    })

    // no glide.js here (see render()'s show/hide-based pagination above),
    // so swipe needs its own minimal touch handling - a plain horizontal
    // swipe gesture on the list, same 50px threshold glide.js itself uses
    const SWIPE_THRESHOLD = 50
    let touchStartX = null
    let touchStartY = null

    listEl.addEventListener('touchstart', (event) => {
        touchStartX = event.touches[0].clientX
        touchStartY = event.touches[0].clientY
    }, { passive: true })

    listEl.addEventListener('touchend', (event) => {
        if (touchStartX === null) {
            return
        }
        const deltaX = event.changedTouches[0].clientX - touchStartX
        const deltaY = event.changedTouches[0].clientY - touchStartY
        touchStartX = null
        touchStartY = null

        // ignore mostly-vertical gestures (page scroll), only react to a
        // clearly horizontal swipe
        if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) {
            return
        }
        goToPage(deltaX < 0 ? 1 : -1)
    }, { passive: true })

    render()
}

export default function InitImageSlider() {
    document.querySelectorAll('[data-image-slider]').forEach((galleryEl) => {
        if (galleryEl.dataset.imageSliderLayout === 'double') {
            initDoubleLayout(galleryEl)
        } else {
            initSingleLayout(galleryEl)
        }
    })
}
